import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ShowcaseItem } from '../../types';
import {
  adminListShowcase,
  adminCreateShowcase,
  adminUpdateShowcase,
  adminDeleteShowcase,
  uploadShowcaseThumbnail,
  uploadShowcaseVideo,
} from '../../api';
import Breadcrumb from '../../components/Breadcrumb';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';
import { tosAccelerate } from '../../utils/tosAccelerate';
import { useImageUploadZone } from '../../hooks/useImageUploadZone';
import { useImageAspectRatioGuard } from '../../hooks/useImageAspectRatioGuard';
import { getDropHighlightStyle } from '../../utils/imageFiles';
import { UploadZoneHint } from '../../components/UploadZoneHint';

type ShowcaseLanguage = 'zh' | 'en' | 'all';
type LanguageFilter = '' | ShowcaseLanguage;
type VideoMode = 'upload' | 'url';

interface ShowcaseForm {
  title: string;
  description: string;
  language: ShowcaseLanguage;
  sortOrder: number;
  isPublished: boolean;
  thumbnailUrl: string;
  videoUrl: string;
}

const EMPTY_FORM: ShowcaseForm = {
  title: '',
  description: '',
  language: 'zh',
  sortOrder: 0,
  isPublished: false,
  thumbnailUrl: '',
  videoUrl: '',
};

const TOS_DOMAIN = '.volces.com';

const isTosUrl = (url: string): boolean => url.includes(TOS_DOMAIN);
const normalizeShowcaseLanguage = (language: string): ShowcaseLanguage =>
  language === 'en' || language === 'all' ? language : 'zh';

const ShowcaseManager: React.FC = () => {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const guardAspectRatio = useImageAspectRatioGuard();

  const [items, setItems] = useState<ShowcaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [languageFilter, setLanguageFilter] = useState<LanguageFilter>('');

  // modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ShowcaseForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoMode, setVideoMode] = useState<VideoMode>('upload');
  const [uploadedVideoName, setUploadedVideoName] = useState('');

  // confirm delete
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; title: string } | null>(null);

  // file input refs
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const loadData = useCallback(() => {
    setLoading(true);
    const lang = languageFilter || undefined;
    adminListShowcase(lang)
      .then(res => {
        setItems(res.items || []);
      })
      .catch(() => toast.error(t('showcase.loadFailed')))
      .finally(() => setLoading(false));
  }, [languageFilter, toast, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setVideoMode('upload');
    setUploadedVideoName('');
    setShowModal(true);
  };

  const openEdit = (item: ShowcaseItem) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      description: item.description,
      language: normalizeShowcaseLanguage(item.language),
      sortOrder: item.sortOrder,
      isPublished: item.isPublished,
      thumbnailUrl: item.thumbnailUrl,
      videoUrl: item.videoUrl,
    });
    const mode = item.videoUrl && !isTosUrl(item.videoUrl) ? 'url' : 'upload';
    setVideoMode(mode);
    setUploadedVideoName(mode === 'upload' && item.videoUrl ? item.videoUrl.split('/').pop() || '' : '');
    setShowModal(true);
  };

  const uploadThumbnailFile = async (file: File) => {
    if (!(await guardAspectRatio(file))) return;
    setUploadingThumbnail(true);
    try {
      const res = await uploadShowcaseThumbnail(file);
      setForm(prev => ({ ...prev, thumbnailUrl: res.url }));
    } catch {
      toast.error(t('showcase.operationFailed'));
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await uploadThumbnailFile(file);
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
  };

  const thumbnailZone = useImageUploadZone({
    onFiles: (imgs) => uploadThumbnailFile(imgs[0]),
    disabled: uploadingThumbnail,
  });

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    try {
      const res = await uploadShowcaseVideo(file);
      setForm(prev => ({ ...prev, videoUrl: res.url }));
      setUploadedVideoName(file.name);
    } catch {
      toast.error(t('showcase.operationFailed'));
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error(t('showcase.titleEmpty'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        language: form.language,
        sortOrder: form.sortOrder,
        isPublished: form.isPublished,
        thumbnailUrl: form.thumbnailUrl,
        videoUrl: form.videoUrl.trim(),
      };
      if (editingId !== null) {
        await adminUpdateShowcase(editingId, payload);
        toast.success(t('showcase.updateSuccess'));
      } else {
        await adminCreateShowcase(payload);
        toast.success(t('showcase.createSuccess'));
      }
      setShowModal(false);
      loadData();
    } catch {
      toast.error(t('showcase.operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await adminDeleteShowcase(confirmDelete.id);
      toast.success(t('showcase.deleteSuccess'));
      loadData();
    } catch {
      toast.error(t('showcase.operationFailed'));
    }
    setConfirmDelete(null);
  };

  const isUploading = uploadingThumbnail || uploadingVideo;

  const languageLabel = (lang: string): string => {
    if (lang === 'all') return t('showcase.allLanguageType');
    if (lang === 'zh') return t('showcase.chinese');
    if (lang === 'en') return t('showcase.english');
    return lang;
  };

  return (
    <div className="container" style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem' }}>
      <Breadcrumb items={[
        { label: t('showcase.breadcrumbHome'), to: '/projects' },
        { label: t('showcase.title') },
      ]} />

      <div className="page-header">
        <h1 className="page-title">{t('showcase.title')}</h1>
        <button className="btn btn-primary" onClick={openCreate}>
          {t('showcase.createItem')}
        </button>
      </div>

      {/* Language filter */}
      <div style={{ marginBottom: '1.25rem' }}>
        <select
          className="input"
          value={languageFilter}
          onChange={e => setLanguageFilter(e.target.value as LanguageFilter)}
          style={{ width: 'auto', minWidth: 160 }}
        >
          <option value="">{t('showcase.allLanguages')}</option>
          <option value="all">{t('showcase.allLanguageType')}</option>
          <option value="zh">{t('showcase.chinese')}</option>
          <option value="en">{t('showcase.english')}</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>
          {t('loading', { ns: 'common' })}
        </div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>
          {t('showcase.noItems')}
        </div>
      ) : (
        <>
          <style>{`
            .showcase-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 1rem;
            }
            @media (max-width: 900px) {
              .showcase-grid { grid-template-columns: repeat(2, 1fr); }
            }
            @media (max-width: 560px) {
              .showcase-grid { grid-template-columns: 1fr; }
            }
          `}</style>
          <div className="showcase-grid">
            {items.map(item => (
              <div
                key={item.id}
                className="card"
                style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
              >
                {/* Thumbnail */}
                <div style={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '56.25%',
                  backgroundColor: 'var(--surface-2)',
                  overflow: 'hidden',
                }}>
                  {item.thumbnailUrl ? (
                    <img
                      src={tosAccelerate(item.thumbnailUrl)}
                      alt={item.title}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--text-3)',
                      fontSize: '0.8rem',
                    }}>
                      No Image
                    </div>
                  )}

                  {/* Published badge - top left */}
                  <span style={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    fontSize: '0.68rem',
                    padding: '0.15rem 0.5rem',
                    borderRadius: '4px',
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: item.isPublished ? 'var(--success)' : 'var(--text-3)',
                  }}>
                    {item.isPublished ? t('showcase.published') : t('showcase.unpublished')}
                  </span>

                  {/* Language badge - top right */}
                  <span style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    fontSize: '0.68rem',
                    padding: '0.15rem 0.5rem',
                    borderRadius: '4px',
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: 'rgba(0,0,0,0.55)',
                  }}>
                    {languageLabel(item.language)}
                  </span>
                </div>

                {/* Card body */}
                <div style={{ padding: '0.75rem 1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                    {item.title}
                  </div>
                  {item.description && (
                    <div style={{
                      fontSize: '0.82rem',
                      color: 'var(--text-3)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical' as const,
                      marginBottom: '0.35rem',
                    }}>
                      {item.description}
                    </div>
                  )}
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 'auto' }}>
                    {t('showcase.sortOrder')}: {item.sortOrder}
                  </div>
                </div>

                {/* Actions */}
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem 0.75rem',
                  borderTop: '1px solid var(--border)',
                }}>
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: '0.78rem' }}
                    onClick={() => openEdit(item)}
                  >
                    {t('showcase.edit')}
                  </button>
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: '0.78rem', color: 'var(--danger)' }}
                    onClick={() => setConfirmDelete({ id: item.id, title: item.title })}
                  >
                    {t('showcase.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create/Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingId !== null ? t('showcase.editItem') : t('showcase.createItemTitle')}
        width="580px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Title */}
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.3rem', display: 'block' }}>
              {t('showcase.titleLabel')}
            </label>
            <input
              className="input"
              value={form.title}
              onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder={t('showcase.titlePlaceholder')}
              style={{ width: '100%' }}
            />
          </div>

          {/* Description */}
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.3rem', display: 'block' }}>
              {t('showcase.descriptionLabel')}
            </label>
            <textarea
              className="input"
              value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('showcase.descriptionPlaceholder')}
              rows={3}
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>

          {/* Language + Sort Order row */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 140px', minWidth: 0 }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.3rem', display: 'block' }}>
                {t('showcase.languageLabel')}
              </label>
              <select
                className="input"
                value={form.language}
                onChange={e => setForm(prev => ({ ...prev, language: e.target.value as ShowcaseLanguage }))}
                style={{ width: '100%' }}
              >
                <option value="all">{t('showcase.allLanguageType')}</option>
                <option value="zh">{t('showcase.chinese')}</option>
                <option value="en">{t('showcase.english')}</option>
              </select>
            </div>
            <div style={{ flex: '1 1 140px', minWidth: 0 }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.3rem', display: 'block' }}>
                {t('showcase.sortOrderLabel')}
              </label>
              <input
                className="input"
                type="number"
                value={form.sortOrder}
                onChange={e => setForm(prev => ({ ...prev, sortOrder: Number(e.target.value) || 0 }))}
                style={{ width: '100%' }}
              />
              <div style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginTop: '0.2rem' }}>
                {t('showcase.sortOrderHint')}
              </div>
            </div>
          </div>

          {/* Published toggle */}
          <div>
            <label style={{
              fontSize: '0.82rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={form.isPublished}
                onChange={e => setForm(prev => ({ ...prev, isPublished: e.target.checked }))}
              />
              {t('showcase.publishLabel')}
            </label>
          </div>

          {/* Thumbnail upload */}
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.3rem', display: 'block' }}>
              {t('showcase.thumbnailLabel')}
            </label>
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/*"
              onChange={handleThumbnailUpload}
              style={{ display: 'none' }}
            />
            {form.thumbnailUrl ? (
              <div>
                <div style={{
                  width: '100%',
                  paddingTop: '56.25%',
                  position: 'relative',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  border: '1px solid var(--border)',
                }}>
                  <img
                    src={tosAccelerate(form.thumbnailUrl)}
                    alt="thumbnail preview"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
                <button
                  className="btn btn-ghost"
                  {...thumbnailZone.zoneProps}
                  style={{ fontSize: '0.78rem', marginTop: '0.4rem', ...getDropHighlightStyle(thumbnailZone.isOver) }}
                  onClick={() => thumbnailInputRef.current?.click()}
                  disabled={uploadingThumbnail}
                >
                  {uploadingThumbnail ? t('showcase.uploading') : t('showcase.thumbnailChange')}
                </button>
              </div>
            ) : (
              <div
                {...thumbnailZone.zoneProps}
                onClick={() => { if (!uploadingThumbnail) thumbnailInputRef.current?.click(); }}
                style={{
                  border: '2px dashed var(--border)',
                  borderRadius: '6px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  cursor: uploadingThumbnail ? 'default' : 'pointer',
                  color: 'var(--text-3)',
                  fontSize: '0.82rem',
                  ...getDropHighlightStyle(thumbnailZone.isOver),
                }}
              >
                {uploadingThumbnail ? (
                  t('showcase.uploading')
                ) : (
                  <>
                    {t('showcase.thumbnailUpload')}
                    <div>
                      <UploadZoneHint />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Video section */}
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.3rem', display: 'block' }}>
              {t('showcase.videoLabel')}
            </label>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: '0.75rem' }}>
              <button
                className={videoMode === 'upload' ? 'btn btn-primary' : 'btn btn-ghost'}
                style={{
                  fontSize: '0.78rem',
                  borderRadius: '4px 0 0 4px',
                  borderRight: videoMode === 'upload' ? undefined : '1px solid var(--border)',
                }}
                onClick={() => setVideoMode('upload')}
              >
                {t('showcase.videoUploadTab')}
              </button>
              <button
                className={videoMode === 'url' ? 'btn btn-primary' : 'btn btn-ghost'}
                style={{
                  fontSize: '0.78rem',
                  borderRadius: '0 4px 4px 0',
                }}
                onClick={() => setVideoMode('url')}
              >
                {t('showcase.videoUrlTab')}
              </button>
            </div>

            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              onChange={handleVideoUpload}
              style={{ display: 'none' }}
            />

            {videoMode === 'upload' ? (
              uploadedVideoName || (form.videoUrl && isTosUrl(form.videoUrl)) ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: '0.82rem',
                    color: 'var(--text-2)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 300,
                  }}>
                    {uploadedVideoName || form.videoUrl.split('/').pop() || 'video'}
                  </span>
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: '0.78rem' }}
                    onClick={() => videoInputRef.current?.click()}
                    disabled={uploadingVideo}
                  >
                    {uploadingVideo ? t('showcase.uploading') : t('showcase.videoChange')}
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => { if (!uploadingVideo) videoInputRef.current?.click(); }}
                  style={{
                    border: '2px dashed var(--border)',
                    borderRadius: '6px',
                    padding: '1.5rem',
                    textAlign: 'center',
                    cursor: uploadingVideo ? 'default' : 'pointer',
                    color: 'var(--text-3)',
                    fontSize: '0.82rem',
                  }}
                >
                  {uploadingVideo ? t('showcase.uploading') : t('showcase.videoUpload')}
                </div>
              )
            ) : (
              <input
                className="input"
                value={form.videoUrl}
                onChange={e => setForm(prev => ({ ...prev, videoUrl: e.target.value }))}
                placeholder={t('showcase.videoUrlPlaceholder')}
                style={{ width: '100%' }}
              />
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button className="btn btn-ghost" onClick={() => setShowModal(false)}>
              {t('cancel', { ns: 'common' })}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || isUploading}
            >
              {saving ? t('saving', { ns: 'common' }) : t('save', { ns: 'common' })}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!confirmDelete}
        title={t('showcase.confirmDelete')}
        message={t('showcase.deleteMessage', { title: confirmDelete?.title })}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
        confirmText={t('delete', { ns: 'common' })}
        confirmDanger={true}
      />
    </div>
  );
};

export default ShowcaseManager;
